// PATH: src/components/chapters/chapterFilters.ts
/**
 * Filtres de l'écran « Chapitres » : logique pure (testée), sans React.
 *
 * Les filtres vivent dans l'URL (`program`, `grade`, `subject`, `track`, `q`,
 * `state`, `issue`, `review`, et `doc` pour le panneau de détail ouvert) : la page Couverture y
 * envoie des liens, et un lien partagé rouvre le même écran.
 */
import type {
  CurriculumGrade,
  CurriculumProgram,
  CurriculumStage,
  Document,
  Subject,
  Track,
} from "../../models/domain";
import { offeringAppliesToTrack, sortTracks } from "../curriculum/tracks";
import { asIssueFilter, matchesIssue, type ChapterIssue, type IssueFilter } from "../coverage/chapterIssues";
import { asReviewFilter, matchesReview, type ReviewFilter } from "./reviewStatus";

export type ChapterState = "all" | "active" | "inactive";

/** Valeur du filtre « Filière » pour les seuls chapitres communs à tout le niveau. */
export const COMMON_TRACK = "common";

export interface ChapterFilters {
  program: string;
  grade: string;
  subject: string;
  /** Identifiant de filière, `COMMON_TRACK`, ou "" (toutes). */
  track: string;
  q: string;
  state: ChapterState;
  /** Chapitres à compléter : `all`, `any` ou un défaut précis. */
  issue: IssueFilter;
  /** Relecture humaine : `all` ou un état. */
  review: ReviewFilter;
}

export const EMPTY_FILTERS: ChapterFilters = {
  program: "",
  grade: "",
  subject: "",
  track: "",
  q: "",
  state: "all",
  issue: "all",
  review: "all",
};

const FILTER_KEYS = ["program", "grade", "subject", "track", "q"] as const;

const asState = (raw: string | null): ChapterState =>
  raw === "active" || raw === "inactive" ? raw : "all";

export const filtersFromParams = (params: URLSearchParams): ChapterFilters => ({
  program: params.get("program") ?? "",
  grade: params.get("grade") ?? "",
  subject: params.get("subject") ?? "",
  track: params.get("track") ?? "",
  q: params.get("q") ?? "",
  state: asState(params.get("state")),
  issue: asIssueFilter(params.get("issue")),
  review: asReviewFilter(params.get("review")),
});

/**
 * Réécrit les filtres dans `params` (les autres clés, `doc` compris, sont
 * gardées). Une valeur vide n'apparaît pas ; `state=all`, `issue=all` et `review=all` non plus.
 */
export const filtersToParams = (
  filters: ChapterFilters,
  base: URLSearchParams = new URLSearchParams(),
): URLSearchParams => {
  const next = new URLSearchParams(base);
  FILTER_KEYS.forEach((key) => {
    const value = filters[key].trim();
    if (value) next.set(key, key === "q" ? filters.q : value);
    else next.delete(key);
  });
  if (filters.state !== "all") next.set("state", filters.state);
  else next.delete("state");
  if (filters.issue !== "all") next.set("issue", filters.issue);
  else next.delete("issue");
  if (filters.review !== "all") next.set("review", filters.review);
  else next.delete("review");
  return next;
};

export const sameFilters = (a: ChapterFilters, b: ChapterFilters): boolean =>
  FILTER_KEYS.every((k) => a[k] === b[k]) && a.state === b.state && a.issue === b.issue && a.review === b.review;

/* ---------- Catalogue ---------- */

export interface GradeEntry {
  stage: CurriculumStage["stage"];
  grade: CurriculumGrade;
}

export const findProgram = (programs: CurriculumProgram[], id: string) =>
  programs.find((p) => p.program.id === id);

export const findGrade = (program: CurriculumProgram | undefined, gradeId: string): GradeEntry | undefined => {
  if (!program || !gradeId) return undefined;
  for (const st of program.stages) {
    const g = st.grades.find((x) => x.grade.id === gradeId);
    if (g) return { stage: st.stage, grade: g };
  }
  return undefined;
};

const bySortOrder = <T extends { sort_order?: number; name: string }>(a: T, b: T) =>
  (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name);

/** Niveaux groupés par étape, dans l'ordre du catalogue. */
export const gradeGroups = (program: CurriculumProgram | undefined) =>
  [...(program?.stages ?? [])]
    .sort((a, b) => bySortOrder(a.stage, b.stage))
    .map((st) => ({
      stage: st.stage,
      grades: [...st.grades].sort((a, b) => bySortOrder(a.grade, b.grade)),
    }))
    .filter((g) => g.grades.length > 0);

/** Filières du niveau, triées ; vide si le niveau n'en a pas. */
export const gradeTracks = (entry: GradeEntry | undefined): Track[] =>
  sortTracks(entry?.grade.tracks ?? []);

/**
 * Matières proposées : les offres du niveau (restreintes à la filière choisie
 * le cas échéant), sinon toutes les matières du programme.
 */
export const subjectOptions = (
  program: CurriculumProgram | undefined,
  entry: GradeEntry | undefined,
  track: string,
): Subject[] => {
  if (!program) return [];
  if (!entry) return [...program.subjects].sort(bySortOrder);
  const byId = new Map(program.subjects.map((s) => [s.id, s]));
  const offerings = [...(entry.grade.offerings ?? [])].sort(
    (a, b) => (a.offering.sort_order ?? 0) - (b.offering.sort_order ?? 0),
  );
  const out: Subject[] = [];
  const seen = new Set<string>();
  offerings.forEach(({ offering, subject }) => {
    if (track && track !== COMMON_TRACK && !offeringAppliesToTrack(offering, track)) return;
    const s = subject ?? byId.get(offering.subject_id);
    if (s && !seen.has(s.id)) {
      seen.add(s.id);
      out.push(s);
    }
  });
  return out;
};

/**
 * Remet les filtres en cohérence avec le catalogue : un programme est TOUJOURS
 * choisi (la liste du serveur l'exige), un niveau d'un autre programme, une
 * filière d'un autre niveau ou une matière non offerte tombent.
 */
export const normalizeFilters = (
  filters: ChapterFilters,
  programs: CurriculumProgram[],
  preferredProgram?: string | null,
): ChapterFilters => {
  if (programs.length === 0) return { ...filters, program: "", grade: "", subject: "", track: "" };
  const programId = findProgram(programs, filters.program)
    ? filters.program
    : preferredProgram && findProgram(programs, preferredProgram)
      ? preferredProgram
      : programs[0].program.id;
  const program = findProgram(programs, programId);

  const entry = findGrade(program, filters.grade);
  const grade = entry ? filters.grade : "";

  const tracks = gradeTracks(entry);
  let track = "";
  if (entry && tracks.length > 0) {
    if (filters.track === COMMON_TRACK || tracks.some((t) => t.id === filters.track)) {
      track = filters.track;
    }
  }

  const subjects = subjectOptions(program, entry, track);
  const subject = subjects.some((s) => s.id === filters.subject) ? filters.subject : "";

  return { ...filters, program: programId, grade, subject, track };
};

/** Paramètres de `GET /v1/documents` pour ces filtres. */
export const buildListParams = (filters: ChapterFilters) => {
  const params: {
    program_id: string;
    grade_id?: string;
    subject_id?: string;
    track_id?: string;
    order: "program";
    limit: number;
  } = { program_id: filters.program, order: "program", limit: 500 };
  if (filters.grade) params.grade_id = filters.grade;
  if (filters.subject) params.subject_id = filters.subject;
  // Une filière : le serveur rend ses chapitres ET les communs (contrat).
  if (filters.track && filters.track !== COMMON_TRACK) params.track_id = filters.track;
  return params;
};

const fold = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/**
 * Filtres appliqués côté client : recherche, état, « tronc commun », défauts,
 * relecture (`review_status` de chaque ligne ; le serveur sait aussi filtrer).
 * `issuesOf` rend les défauts d'une ligne, ou null quand ils ne sont pas
 * connus (complétude non chargée, document hors ossature) : une telle ligne
 * ne répond à aucun filtre de défaut.
 */
export const filterChapters = <
  T extends Pick<Document, "title" | "chapter_ref" | "is_active" | "track_id"> & { review_status?: string | null },
>(
  rows: T[],
  filters: ChapterFilters,
  issuesOf?: (row: T) => ChapterIssue[] | null,
): T[] => {
  const q = fold(filters.q.trim());
  return rows.filter((r) => {
    if (filters.state === "active" && !r.is_active) return false;
    if (filters.state === "inactive" && r.is_active) return false;
    if (filters.track === COMMON_TRACK && r.track_id) return false;
    if (!matchesReview(r.review_status, filters.review)) return false;
    if (filters.issue !== "all") {
      const issues = issuesOf?.(r) ?? null;
      if (!issues || !matchesIssue(issues, filters.issue)) return false;
    }
    if (q && !fold(`${r.title ?? ""} ${r.chapter_ref ?? ""}`).includes(q)) return false;
    return true;
  });
};

/* ---------- Noms ---------- */

export interface CurriculumNames {
  grade: (id?: string | null) => string;
  subject: (id?: string | null) => string;
  /** « Commun » pour null. */
  track: (id?: string | null) => string;
  /** Filières du niveau d'un chapitre (édition de l'identité). */
  tracksOfGrade: (gradeId?: string | null) => Track[];
}

/** Résolution id → nom depuis l'overview ; un id inconnu reste affiché. */
export const curriculumNames = (program: CurriculumProgram | undefined): CurriculumNames => {
  const grades = new Map<string, string>();
  const subjects = new Map<string, string>();
  const tracks = new Map<string, string>();
  const tracksByGrade = new Map<string, Track[]>();
  (program?.subjects ?? []).forEach((s) => subjects.set(s.id, s.name));
  (program?.stages ?? []).forEach((st) =>
    st.grades.forEach((g) => {
      grades.set(g.grade.id, g.grade.name);
      tracksByGrade.set(g.grade.id, sortTracks(g.tracks ?? []));
      (g.tracks ?? []).forEach((t) => tracks.set(t.id, t.name));
      (g.offerings ?? []).forEach((o) => {
        if (o.subject) subjects.set(o.subject.id, o.subject.name);
      });
    }),
  );
  return {
    grade: (id) => (id ? grades.get(id) ?? id : "—"),
    subject: (id) => (id ? subjects.get(id) ?? id : "—"),
    track: (id) => (id ? tracks.get(id) ?? id : "Commun"),
    tracksOfGrade: (gradeId) => (gradeId ? tracksByGrade.get(gradeId) ?? [] : []),
  };
};

/** Clé localStorage du dernier programme choisi. */
export const PROGRAM_STORAGE_KEY = "tc.chapters.program";

export const readStoredProgram = (): string | null => {
  try {
    return window.localStorage.getItem(PROGRAM_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const storeProgram = (id: string) => {
  try {
    window.localStorage.setItem(PROGRAM_STORAGE_KEY, id);
  } catch {
    /* stockage indisponible : on retombe sur le premier programme */
  }
};
